import { z } from 'zod';
import type { SubscribeCampaignConfig } from '../../config/types';

const NAME_REGEX = /^\p{L}+$/u;
const PHONE_REGEX = /^[0-9]{9}$/;
const NOT_BLANK_MESSAGE = 'Ta wartość nie powinna być pusta.';
const CONSENT_REQUIRED_MESSAGE = 'Zgoda wymagana';

/** `z.literal(true, { message })` silently ignores the custom message in this zod version
 *  (falls back to "Invalid literal value, expected true") — `.refine()` doesn't have that bug. */
const requiredConsent = () => z.boolean().refine((v) => v === true, { message: CONSENT_REQUIRED_MESSAGE });

/**
 * Symfony's validator skips format constraints (Regex/Email/Length) entirely when a value is
 * null/blank — only NotBlank fires in that case. Zod's chained `.min().regex()` doesn't
 * short-circuit like that (both checks run and both issues get collected), so a naive
 * translation produces either the wrong message or a duplicate one for blank required fields.
 * These two helpers replicate Symfony's actual precedence: blank+required -> NotBlank message
 * only; non-blank -> format message only if it fails; blank+optional -> always valid.
 */
function requiredPatternField(pattern: RegExp, formatMessage: string) {
  return z
    .string()
    .optional()
    .superRefine((val, ctx) => {
      const v = val ?? '';
      if (!v) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: NOT_BLANK_MESSAGE });
        return;
      }
      if (!pattern.test(v)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: formatMessage });
      }
    });
}

function optionalPatternField(pattern: RegExp, formatMessage: string) {
  return z
    .string()
    .optional()
    .superRefine((val, ctx) => {
      if (!val) return;
      if (!pattern.test(val)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: formatMessage });
      }
    });
}

const nameField = (required: boolean) =>
  required ? requiredPatternField(NAME_REGEX, 'Niepoprawne znaki') : optionalPatternField(NAME_REGEX, 'Niepoprawne znaki');

const emailFormatCheck = z.string().email();

/** Mirrors NotBlank + Email(strict) + Length(max:50) — same "skip format when blank" precedence. */
function emailField() {
  return z
    .string()
    .optional()
    .superRefine((val, ctx) => {
      const v = val ?? '';
      if (!v) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: NOT_BLANK_MESSAGE });
        return;
      }
      if (v.length > 50) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Ta wartość jest za długa. Powinna mieć 50 znaków lub mniej.' });
        return;
      }
      if (!emailFormatCheck.safeParse(v).success) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Ta wartość nie jest prawidłowym adresem email.' });
      }
    });
}

/**
 * Mirrors Symfony's `SubscribeType` field presence + validation rules exactly (see
 * skoda_newsletter_app/src/Form/Type/SubscribeType.php). Fields that don't exist for a given
 * campaign config are simply absent from the schema — no CSS-hide-but-validate quirk.
 */
export function buildSubscribeSchema(config: SubscribeCampaignConfig) {
  const shape: Record<string, z.ZodTypeAny> = {};

  if (!config.hideFirstnameField) shape.firstName = nameField(config.firstnameRequired);
  if (!config.hideLastnameField) shape.lastName = nameField(false);

  if (!config.disableEmailField) {
    shape.email = emailField();
  }

  if (config.showPhone) {
    shape.phone = config.phoneRequired
      ? requiredPatternField(PHONE_REGEX, 'Niepoprawny numer telefonu')
      : optionalPatternField(PHONE_REGEX, 'Niepoprawny numer telefonu');

    if (!config.hideAgreeTestDriveMessage) {
      shape.agreeTestDrive = z.boolean().optional();
    }
  }

  if (!config.disableAgrees) {
    shape.agreeNewsletter = requiredConsent();
    shape.agreeDirectMarketing = requiredConsent();
    shape.agreeRules = requiredConsent();
  }

  if (config.extraCheckbox1) shape.extraCheckbox1 = z.boolean().optional();
  if (config.extraCheckbox2) shape.extraCheckbox2 = z.boolean().optional();

  const schema = z.object(shape);

  // Cross-field rule ported from SubscribeType's dynamic `validation_groups` resolver: the
  // 'phone' validation group activates whenever EITHER phone has a value OR agree_test_drive is
  // checked (`strlen($data->getPhone()) || $data->getAgreeTestDrive()`), and both phone's
  // NotBlank and agree_test_drive's IsTrue carry that same group — so activating it makes BOTH
  // fields required together, symmetrically. `phone_required=true` additionally puts both
  // constraints in the always-active 'Default' group. Neither of the 13 active campaigns sets
  // phoneRequired=true today, but the rule is kept general for future config changes.
  if (config.showPhone && !config.hideAgreeTestDriveMessage) {
    return schema.superRefine((data: any, ctx) => {
      const required = config.phoneRequired || !!data.phone || !!data.agreeTestDrive;
      if (!required) return;
      // Skip when config.phoneRequired already routed `phone` through requiredPatternField()
      // above (which reports its own NotBlank) — avoids a duplicate issue on the same field.
      if (!config.phoneRequired && !data.phone) {
        ctx.addIssue({ path: ['phone'], code: z.ZodIssueCode.custom, message: NOT_BLANK_MESSAGE });
      }
      if (!data.agreeTestDrive) {
        ctx.addIssue({
          path: ['agreeTestDrive'],
          code: z.ZodIssueCode.custom,
          message: CONSENT_REQUIRED_MESSAGE,
        });
      }
    });
  }

  return schema;
}

export type SubscribeFormValues = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  agreeTestDrive?: boolean;
  agreeNewsletter?: boolean;
  agreeDirectMarketing?: boolean;
  agreeRules?: boolean;
  extraCheckbox1?: boolean;
  extraCheckbox2?: boolean;
};
